import { IdcardOutlined, LogoutOutlined, SearchOutlined } from "@ant-design/icons";
import { Modal } from "antd";
import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "react-oidc-context";
import { useNavigate } from "react-router-dom";
import { clearAccessToken } from "../authConfig/token";
import { navGroups } from "../navigation";
import { RouteNames } from "../routes";

interface Command {
    id: string;
    label: string;
    /** Group heading the entry is listed under. */
    section: string;
    icon: ReactNode;
    hint?: string;
    run: () => void;
}

/** Registers the ⌘K / Ctrl+K shortcut for as long as the caller is mounted. */
export function useCommandPaletteHotkey(onOpen: () => void) {
    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key.toLowerCase() === "k" && (event.metaKey || event.ctrlKey)) {
                event.preventDefault();
                onOpen();
            }
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [onOpen]);
}

/**
 * Jump anywhere with the keyboard.
 *
 * The app is a tree of eight sections that an admin moves between all day; the
 * palette makes that a two-keystroke move instead of a trip to the sidebar. It
 * intentionally only navigates — no destructive command is reachable from here
 * except signing out, which is confirmed by the identity provider anyway.
 */
export const CommandPalette = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const auth = useAuth();
    const inputRef = useRef<HTMLInputElement>(null);
    const [query, setQuery] = useState("");
    const [cursor, setCursor] = useState(0);

    const commands: Command[] = useMemo(() => {
        const pages: Command[] = navGroups.flatMap(group =>
            group.items.map(item => ({
                id: item.route,
                label: t(item.labelKey),
                section: t("command.pages"),
                icon: item.icon,
                hint: t(group.labelKey),
                run: () => navigate(`/${item.route}`)
            }))
        );

        return [
            ...pages,
            {
                id: RouteNames.account,
                label: t("menu.account"),
                section: t("command.pages"),
                icon: <IdcardOutlined />,
                run: () => navigate(`/${RouteNames.account}`)
            },
            {
                id: "sign-out",
                label: t("actions.signOut"),
                section: t("command.actions"),
                icon: <LogoutOutlined />,
                run: () => {
                    clearAccessToken();
                    void auth.signoutRedirect({
                        post_logout_redirect_uri: window.location.origin,
                        id_token_hint: auth.user?.id_token
                    });
                }
            }
        ];
    }, [t, navigate, auth]);

    const matches = useMemo(() => {
        const needle = query.trim().toLowerCase();
        if (needle.length === 0) return commands;
        return commands.filter(command =>
            `${command.label} ${command.hint ?? ""}`.toLowerCase().includes(needle)
        );
    }, [commands, query]);

    // A fresh palette every time: a stale query is never what the next jump wants.
    useEffect(() => {
        if (open) {
            setQuery("");
            setCursor(0);
            const focus = window.setTimeout(() => inputRef.current?.focus(), 40);
            return () => window.clearTimeout(focus);
        }
    }, [open]);

    useEffect(() => setCursor(0), [query]);

    const run = (command: Command | undefined) => {
        if (command == null) return;
        onClose();
        command.run();
    };

    const onKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === "ArrowDown") {
            event.preventDefault();
            setCursor(current => (matches.length === 0 ? 0 : (current + 1) % matches.length));
        } else if (event.key === "ArrowUp") {
            event.preventDefault();
            setCursor(current =>
                matches.length === 0 ? 0 : (current - 1 + matches.length) % matches.length
            );
        } else if (event.key === "Enter") {
            event.preventDefault();
            run(matches[cursor]);
        }
    };

    // Section headings are emitted inline so the flat cursor index stays valid.
    let previousSection: string | null = null;

    return (
        <Modal
            className="cmdk-modal"
            visible={open}
            onCancel={onClose}
            footer={null}
            closable={false}
            destroyOnClose
            width={560}
            style={{ top: 96 }}
        >
            <div className="cmdk__input-row">
                <SearchOutlined />
                <input
                    ref={inputRef}
                    className="cmdk__input"
                    placeholder={t("command.placeholder")}
                    value={query}
                    onChange={event => setQuery(event.target.value)}
                    onKeyDown={onKeyDown}
                    aria-label={t("command.placeholder")}
                />
                <span className="kbd">ESC</span>
            </div>

            <div className="cmdk__list" role="listbox">
                {matches.length === 0 ? <div className="cmdk__empty">{t("command.empty")}</div> : null}
                {matches.map((command, index) => {
                    const heading = command.section !== previousSection ? command.section : null;
                    previousSection = command.section;
                    return (
                        <div key={command.id}>
                            {heading != null ? (
                                <div className="cmdk__group-label eyebrow">{heading}</div>
                            ) : null}
                            <button
                                type="button"
                                role="option"
                                aria-selected={index === cursor}
                                className={`cmdk__item${index === cursor ? " is-active" : ""}`}
                                onMouseEnter={() => setCursor(index)}
                                onClick={() => run(command)}
                            >
                                {command.icon}
                                <span>{command.label}</span>
                                {command.hint != null ? (
                                    <span className="cmdk__item-hint">{command.hint}</span>
                                ) : null}
                            </button>
                        </div>
                    );
                })}
            </div>

            <div className="cmdk__foot">
                <span>
                    <span className="kbd">↑</span>
                    <span className="kbd">↓</span>
                    {t("command.navigate")}
                </span>
                <span>
                    <span className="kbd">↵</span>
                    {t("command.select")}
                </span>
            </div>
        </Modal>
    );
};
